import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, Sparkles, CheckCircle, X, Check, Building2, ChevronDown, ChevronUp, Mail, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/hooks/useCompany';
import { useSwitchCompany } from '@/hooks/useSwitchCompany';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { PendingEmailImports } from '@/components/messages/PendingEmailImports';
import { PendingEmailImportsAllCompanies } from '@/components/messages/PendingEmailImportsAllCompanies';
import { ConnectedMailboxes } from '@/components/messages/ConnectedMailboxes';
import { EmailReviewBadge, EmailReviewBanner } from '@/components/messages/EmailReviewBadge';
import NewConversationDialog from '@/components/messages/NewConversationDialog';
import MessageAttachmentList from '@/components/messages/MessageAttachmentList';
import { useFormatters } from '@financialharmony/ui';
import { useRelativeTime } from '@/i18n/useRelativeTime';
import {
  channelDeliveryStampKey,
  channelDeliveryStampTone,
  isChannelDeliveryReason,
  isChannelDeliveryStatus,
  isInFlightChannelDelivery,
} from '@/components/messages/channelDeliveryStamp';
import type {
  ConsultantThread,
  ConsultantMessage,
  EmailReviewStatus,
  ThreadWithMessages,
} from '@/types/messaging';
import { userFacingErrorMessage } from '@/utils/userFacingError';

/** Merge only channel-delivery fields from a poll onto the open thread (Direction A stamps). */
function mergeChannelDeliveryStamps(
  prev: ThreadWithMessages,
  polled: ThreadWithMessages,
): ThreadWithMessages {
  const byId = new Map(polled.messages.map((m) => [m.id, m]));
  return {
    ...prev,
    messages: prev.messages.map((m) => {
      const fresh = byId.get(m.id);
      if (!fresh) return m;
      if (
        fresh.channel_delivery_status === m.channel_delivery_status &&
        (fresh.channel_delivery_reason ?? null) === (m.channel_delivery_reason ?? null)
      ) {
        return m;
      }
      return {
        ...m,
        channel_delivery_status: fresh.channel_delivery_status,
        channel_delivery_reason: fresh.channel_delivery_reason ?? null,
        external_message_id: fresh.external_message_id ?? m.external_message_id,
        rfc_message_id: fresh.rfc_message_id ?? m.rfc_message_id,
        sent_at: fresh.sent_at ?? m.sent_at,
      };
    }),
  };
}

/** The thread pane when no thread is open: the switch prompt for a linked thread in another
 * company, a load error, or the empty state. */
function ThreadPaneNotice({
  needsPlatformView,
  companyName,
  switching,
  onSwitchToPlatformView,
  threadError,
}: {
  needsPlatformView: boolean;
  companyName: string | undefined;
  switching: boolean;
  onSwitchToPlatformView: () => void;
  threadError: string | null;
}) {
  const t = useTranslations('consultantMessages.page');
  if (needsPlatformView) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center text-sm"
        data-testid="linked-thread-needs-platform-view"
      >
        <p className="text-muted-foreground max-w-sm">
          {t('linkedElsewhere', { company: companyName ?? t('selectedCompany') })}
        </p>
        <Button size="sm" onClick={onSwitchToPlatformView} disabled={switching}>
          {switching && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {t('switchToAdminView')}
        </Button>
      </div>
    );
  }
  if (threadError) {
    return <div className="flex-1 flex items-center justify-center text-sm text-destructive">{threadError}</div>;
  }
  return (
    <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
      {t('selectThread')}
    </div>
  );
}

export default function Messages() {
  const t = useTranslations('consultantMessages.page');
  const relativeTime = useRelativeTime();
  const { formatDate } = useFormatters();
  // page is staff-only — PlatformAdminGuard + OnboardingGuard enforce access at route level
  const { platformRole } = useAuth();
  const { company, isPlatformAdminView, loading: companyLoading } = useCompany();

  const [threads, setThreads] = useState<ConsultantThread[]>([]);
  const [activeThread, setActiveThread] = useState<ThreadWithMessages | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'open' | 'resolved'>('open');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [renamingSubject, setRenamingSubject] = useState(false);
  const [suggestingSubject, setSuggestingSubject] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [savingSubject, setSavingSubject] = useState(false);
  const [reviewActing, setReviewActing] = useState(false);
  // Bumped after a review decision made here so the pending-imports queue reloads.
  const [pendingImportsKey, setPendingImportsKey] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  // FIN-1435: `/messages/<threadId>` is the "Open in Harmony" link posted to Slack for every client
  // message, and the URL of whichever thread is open.
  const { threadId: linkedThreadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();
  const [linkedThreadNeedsPlatformView, setLinkedThreadNeedsPlatformView] = useState(false);
  const openedLinkKey = useRef<string | null>(null);
  const { switchTo, pendingKey } = useSwitchCompany();

  useEffect(() => {
    setLoadingThreads(true);
    ServiceRegistry.getMessagingService()
      .listThreads({ status: statusFilter })
      .then(setThreads)
      .catch(() => {})
      .finally(() => setLoadingThreads(false));
  }, [statusFilter]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThread?.messages]);

  // Only the latest open may write the pane: a slower, earlier load (a deep link still in flight
  // when the user clicks another thread) must not overwrite what they chose after it.
  const latestThreadRequest = useRef(0);

  // Direction A: while a channel leg is pending/retrying, quietly refresh so the stamp can
  // move to sent/failed without a loud banner or toast. Does not touch latestThreadRequest —
  // only merges when this thread is still the open one.
  const openThreadId = activeThread?.thread.id;
  const hasInFlightChannelDelivery =
    activeThread?.messages.some((m) => isInFlightChannelDelivery(m.channel_delivery_status)) ??
    false;
  useEffect(() => {
    if (!openThreadId || !hasInFlightChannelDelivery) return;

    let cancelled = false;
    let inFlight = false;
    const interval = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void ServiceRegistry.getMessagingService()
        .getThread(openThreadId)
        .then((data) => {
          if (cancelled) return;
          // Merge delivery stamps only — never clobber newer local messages with a slow poll.
          setActiveThread((prev) => {
            if (!prev || prev.thread.id !== openThreadId) return prev;
            return mergeChannelDeliveryStamps(prev, data);
          });
        })
        .catch(() => {
          // Stamp refresh is best-effort; leave the last known messages in place.
        })
        .finally(() => {
          inFlight = false;
        });
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [openThreadId, hasInFlightChannelDelivery]);

  const openThreadById = async (threadId: string): Promise<'opened' | 'failed' | 'superseded'> => {
    const request = ++latestThreadRequest.current;
    setLoadingMessages(true);
    setBody('');
    setDraftError(null);
    setThreadError(null);
    setLinkedThreadNeedsPlatformView(false);
    setRenamingSubject(false);
    setContextExpanded(false);
    let outcome: 'opened' | 'failed';
    let data: ThreadWithMessages | null = null;
    try {
      data = await ServiceRegistry.getMessagingService().getThread(threadId);
      outcome = 'opened';
    } catch {
      outcome = 'failed';
    }
    if (request !== latestThreadRequest.current) return 'superseded';
    setActiveThread(data);
    if (outcome === 'failed') setThreadError(t('loadFailed'));
    setLoadingMessages(false);
    return outcome;
  };

  // Opened once per (thread, view). A thread outside the selected company is hidden by RLS, so a
  // platform admin outside platform admin view is offered the switch instead of a bare error; a
  // consultant has no such view to switch to.
  useEffect(() => {
    if (!linkedThreadId || companyLoading) return;
    const key = `${linkedThreadId}:${isPlatformAdminView}`;
    if (openedLinkKey.current === key) return;
    openedLinkKey.current = key;
    void openThreadById(linkedThreadId).then((outcome) => {
      if (outcome === 'failed' && !isPlatformAdminView && platformRole === 'platform_admin') {
        setLinkedThreadNeedsPlatformView(true);
      }
    });
  }, [linkedThreadId, companyLoading, isPlatformAdminView, platformRole]);

  const switchToPlatformViewForLinkedThread = () => {
    if (!linkedThreadId) return;
    void switchTo(null, undefined, { redirectTo: `/messages/${encodeURIComponent(linkedThreadId)}` });
  };

  // Keeps the URL on the open thread so it can be shared or reopened; the key tells the effect
  // above this thread is already being opened here.
  const showThreadInUrl = (threadId: string) => {
    openedLinkKey.current = `${threadId}:${isPlatformAdminView}`;
    navigate(`/messages/${encodeURIComponent(threadId)}`, { replace: true });
  };

  const openThread = (threadId: string) => {
    showThreadInUrl(threadId);
    return openThreadById(threadId);
  };

  const selectThread = (thread: ConsultantThread) => openThread(thread.id);

  const applyReviewStatus = (threadId: string, status: EmailReviewStatus) => {
    setActiveThread((prev) =>
      prev && prev.thread.id === threadId && prev.thread.email_review
        ? { ...prev, thread: { ...prev.thread, email_review: { ...prev.thread.email_review, status } } }
        : prev,
    );
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId && t.email_review ? { ...t, email_review: { ...t.email_review, status } } : t)),
    );
  };

  const handleReviewDecision = async (decision: 'approve' | 'reject') => {
    const review = activeThread?.thread.email_review;
    if (!activeThread || !review || reviewActing) return;
    const threadId = activeThread.thread.id;
    setReviewActing(true);
    try {
      const emailThreads = ServiceRegistry.getEmailThreadService();
      if (decision === 'approve') {
        await emailThreads.approveConnection(review.connection_id);
        applyReviewStatus(threadId, 'connected');
        toast.success(t('emailReview.toast.approved'));
      } else {
        await emailThreads.rejectConnection(review.connection_id);
        applyReviewStatus(threadId, 'rejected');
        toast.success(t('emailReview.toast.rejected'));
      }
      setPendingImportsKey((k) => k + 1);
    } catch (err) {
      toast.error(
        userFacingErrorMessage(err, decision === 'approve' ? t('emailReview.toast.approveFailed') : t('emailReview.toast.rejectFailed')),
      );
    } finally {
      setReviewActing(false);
    }
  };

  const handleSend = async () => {
    const text = body.trim();
    if (!text || sending || !activeThread) return;
    setSending(true);
    setBody('');
    try {
      const message = await ServiceRegistry.getMessagingService().sendMessage({
        threadId: activeThread.thread.id,
        body: text,
      });
      setActiveThread((prev) =>
        prev ? { ...prev, messages: [...prev.messages, message] } : prev,
      );
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === activeThread.thread.id
            ? { ...thread, last_message_at: new Date().toISOString() }
            : thread,
        ),
      );
    } catch {
      setBody(text);
    } finally {
      setSending(false);
    }
  };

  const handleGenerateDraft = async () => {
    if (!activeThread) return;
    setGeneratingDraft(true);
    setDraftError(null);
    try {
      const draft = await ServiceRegistry.getMessagingService().generateAiReply(activeThread.thread.id);
      setBody(draft);
    } catch {
      setDraftError(t('draftFailed'));
    } finally {
      setGeneratingDraft(false);
    }
  };

  const handleSuggestSubject = async () => {
    if (!activeThread) return;
    setSuggestingSubject(true);
    try {
      const suggested = await ServiceRegistry.getMessagingService().suggestSubject(activeThread.thread.id);
      setEditedSubject(suggested);
      setRenamingSubject(true);
    } catch (err) {
      toast.error(userFacingErrorMessage(err, t('suggestFailed')));
    } finally {
      setSuggestingSubject(false);
    }
  };

  const handleSaveSubject = async () => {
    if (!activeThread || !editedSubject.trim()) return;
    setSavingSubject(true);
    try {
      const updated = await ServiceRegistry.getMessagingService().updateSubject(activeThread.thread.id, editedSubject.trim());
      setActiveThread((prev) => prev ? { ...prev, thread: updated } : prev);
      setThreads((prev) => prev.map((thread) => thread.id === updated.id ? { ...thread, subject: updated.subject } : thread));
      setRenamingSubject(false);
    } catch {
      // leave rename mode open so user can retry
    } finally {
      setSavingSubject(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!activeThread) return;
    try {
      const updated = await ServiceRegistry.getMessagingService().updateThreadStatus(activeThread.thread.id, 'resolved');
      setActiveThread((prev) => prev ? { ...prev, thread: updated } : prev);
      setThreads((prev) => prev.filter((thread) => thread.id !== updated.id));
    } catch {
      // status update failure is non-critical; thread stays open in UI
    }
  };

  const handleThreadCreated = (data: ThreadWithMessages) => {
    latestThreadRequest.current++; // no load still in flight may replace the thread just created
    showThreadInUrl(data.thread.id);
    setThreads((prev) => [data.thread, ...prev]);
    setActiveThread(data);
    setLoadingMessages(false);
    setStatusFilter('open');
    setShowNewConversation(false);
  };

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="px-6 pt-20 pb-4 border-b bg-background shrink-0 flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight shrink-0">{t('title')}</h1>
        <ConnectedMailboxes
          companyId={isPlatformAdminView ? null : company?.id ?? null}
          companyName={company?.name}
        />
      </div>

      {isPlatformAdminView ? (
        <div className="px-6 pt-4 shrink-0">
          <PendingEmailImportsAllCompanies key={pendingImportsKey} onOpenThread={openThread} />
        </div>
      ) : company?.id ? (
        <div className="px-6 pt-4 shrink-0">
          <PendingEmailImports key={pendingImportsKey} companyId={company.id} onOpenThread={openThread} />
        </div>
      ) : null}

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Thread list */}
        <div className="w-72 border-r flex flex-col shrink-0 overflow-hidden">
          <div className="px-3 pt-3 pb-2 border-b shrink-0">
            <Button
              size="sm"
              className="w-full"
              onClick={() => setShowNewConversation(true)}
              disabled={companyLoading || (!isPlatformAdminView && !company)}
            >
              <Plus className="mr-1 h-3 w-3" /> {t('newConversation')}
            </Button>
          </div>
          <div className="px-3 py-2 border-b flex gap-2 shrink-0">
            <Button
              size="sm"
              variant={statusFilter === 'open' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('open')}
              className="flex-1"
            >
              {t('open')}
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'resolved' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('resolved')}
              className="flex-1"
            >
              {t('resolved')}
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loadingThreads ? (
              <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('loading')}
              </div>
            ) : threads.length === 0 ? (
              <p className="px-3 pt-4 text-sm text-muted-foreground">{t('noThreads')}</p>
            ) : (
              threads.map((thread) => (
                <button
                  key={thread.id}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                    activeThread?.thread.id === thread.id ? 'bg-muted' : ''
                  }`}
                  onClick={() => selectThread(thread)}
                >
                  {(isPlatformAdminView && thread.companies?.name) || thread.source_type === 'email' ? (
                    <div className="mb-0.5 flex flex-wrap items-center gap-1">
                      {isPlatformAdminView && thread.companies?.name && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-medium text-primary border-primary/30 bg-primary/5">
                          {thread.companies.name}
                        </Badge>
                      )}
                      {thread.source_type === 'email' && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-medium gap-0.5 text-muted-foreground">
                          <Mail className="h-2.5 w-2.5" />
                          {t('email')}
                        </Badge>
                      )}
                      {thread.source_type === 'email' && thread.email_review && (
                        <EmailReviewBadge status={thread.email_review.status} />
                      )}
                    </div>
                  ) : null}
                  <p className="font-medium truncate">
                    {thread.subject ?? <span className="italic text-muted-foreground font-normal">{t('untitled')}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{relativeTime(thread.last_message_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread detail */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {loadingMessages ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('loadingMessages')}
            </div>
          ) : activeThread ? (
            <>
              <div className="px-4 py-3 border-b flex items-center justify-between gap-3 shrink-0">
                <div className="flex-1 min-w-0">
                  {renamingSubject ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editedSubject}
                        onChange={(e) => setEditedSubject(e.target.value)}
                        className="h-7 text-sm font-semibold"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveSubject();
                          if (e.key === 'Escape') setRenamingSubject(false);
                        }}
                      />
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={handleSaveSubject} disabled={savingSubject || !editedSubject.trim()}>
                        {savingSubject ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => setRenamingSubject(false)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <h2 className="font-semibold text-foreground truncate">
                        {activeThread.thread.subject ?? <span className="italic font-normal text-muted-foreground">{t('untitled')}</span>}
                      </h2>
                      {activeThread.thread.source_type === 'email' && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-medium gap-0.5 text-muted-foreground shrink-0">
                          <Mail className="h-2.5 w-2.5" />
                          {t('importedFromEmail')}
                        </Badge>
                      )}
                      {activeThread.thread.source_type === 'email' && activeThread.thread.email_review && (
                        <EmailReviewBadge status={activeThread.thread.email_review.status} />
                      )}
                      {activeThread.connectionCount > 1 && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 h-4 font-medium text-destructive border-destructive/30 bg-destructive/5 shrink-0"
                          title={t('sharedMailboxesHint')}
                        >
                          {t('sharedMailboxes', { count: String(activeThread.connectionCount) })}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleSuggestSubject}
                        disabled={suggestingSubject}
                        title={t('suggestName')}
                      >
                        {suggestingSubject ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      </Button>
                    </div>
                  )}
                  {isPlatformAdminView && activeThread.thread.companies?.name && (
                    <p className="text-xs text-muted-foreground">
                      {activeThread.thread.companies.name}
                    </p>
                  )}
                </div>
                {activeThread.thread.status === 'open' && (
                  <Button size="sm" variant="outline" onClick={handleMarkResolved} className="shrink-0">
                    <CheckCircle className="mr-1 h-3 w-3" /> {t('markResolved')}
                  </Button>
                )}
              </div>

              {activeThread.thread.source_type === 'email' && activeThread.thread.email_review && (
                <EmailReviewBanner
                  status={activeThread.thread.email_review.status}
                  acting={reviewActing}
                  onApprove={() => void handleReviewDecision('approve')}
                  onReject={() => void handleReviewDecision('reject')}
                />
              )}

              {activeThread.companyContext && (
                <div className="border-b bg-muted/40 shrink-0">
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => setContextExpanded((v) => !v)}
                  >
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span>{t('companyContext')}</span>
                    {contextExpanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                  </button>
                  {contextExpanded && (
                    <div className="max-h-40 overflow-y-auto px-4 pb-3">
                      <div className="prose prose-sm max-w-none dark:prose-invert text-muted-foreground [&_*]:text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{activeThread.companyContext}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
                {activeThread.messages.map((m: ConsultantMessage) => {
                  const isConsultant = m.sender_role === 'consultant';
                  const deliveryStatus = isChannelDeliveryStatus(m.channel_delivery_status)
                    ? m.channel_delivery_status
                    : null;
                  const stampTone = deliveryStatus
                    ? channelDeliveryStampTone(deliveryStatus)
                    : null;
                  const stampReason = isChannelDeliveryReason(m.channel_delivery_reason)
                    ? m.channel_delivery_reason
                    : null;
                  const stampLabel = deliveryStatus
                    ? t(`stamp.${channelDeliveryStampKey(deliveryStatus, stampReason)}`)
                    : null;
                  let stampTimeClass = 'text-muted-foreground';
                  if (isConsultant && stampTone === 'attention') {
                    stampTimeClass = 'text-primary-foreground/90';
                  } else if (isConsultant) {
                    stampTimeClass = 'text-primary-foreground/70';
                  }
                  return (
                    <div
                      key={m.id}
                      className={`flex ${isConsultant ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded px-3 py-2 text-sm whitespace-pre-wrap ${
                          isConsultant
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted border text-foreground'
                        }`}
                      >
                        {m.from_email && (
                          <p
                            className={`text-xs font-medium mb-0.5 ${
                              isConsultant ? 'text-primary-foreground/80' : 'text-foreground/80'
                            }`}
                          >
                            {m.from_email}
                          </p>
                        )}
                        <p>{m.body}</p>
                        <MessageAttachmentList attachments={m.attachments} isConsultant={isConsultant} />
                        <p
                          className={`text-xs mt-1 text-right ${stampTimeClass}`}
                          {...(deliveryStatus
                            ? {
                                'data-testid': 'channel-delivery-stamp',
                                'data-status': deliveryStatus,
                              }
                            : {})}
                        >
                          {m.sender_role === 'client' ? t('clientPrefix') : ''}
                          {formatDate(m.created_at, 'time')}
                          {stampLabel ? ` · ${stampLabel}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {activeThread.thread.status === 'open' && (
                <div className="shrink-0 px-4 py-3 border-t">
                  {activeThread.thread.source_type === 'email' && (
                    <p
                      data-testid="channel-compose-notice"
                      className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <Mail className="h-3 w-3 shrink-0" />
                      {t('channelNotice')}
                    </p>
                  )}
                  <Card>
                    <CardContent className="pt-4 space-y-3">
                      <Textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={t('replyPlaceholder')}
                        className="min-h-[72px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !sending) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                      />
                      {draftError && (
                        <p className="text-sm text-destructive">{draftError}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleGenerateDraft}
                          disabled={generatingDraft}
                        >
                          {generatingDraft ? (
                            <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> {t('generating')}</>
                          ) : (
                            <><Sparkles className="mr-1 h-3 w-3" /> {t('aiDraft')}</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSend}
                          disabled={!body.trim() || sending}
                        >
                          {sending ? t('sending') : t('send')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <ThreadPaneNotice
              needsPlatformView={linkedThreadNeedsPlatformView}
              companyName={company?.name}
              switching={pendingKey !== null}
              onSwitchToPlatformView={switchToPlatformViewForLinkedThread}
              threadError={threadError}
            />
          )}
        </div>
      </div>

      {showNewConversation && (
        <NewConversationDialog
          isPlatformAdminView={isPlatformAdminView}
          activeCompanyId={company?.id ?? null}
          onCreated={handleThreadCreated}
          onClose={() => setShowNewConversation(false)}
        />
      )}
    </div>
  );
}
